import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api-client';
import { MainLayout } from '../components/MainLayout';

export function BankAccountsPage() {
  const queryClient = useQueryClient();
  const [showAddForm, setShowAddForm] = useState(false);
  const [formData, setFormData] = useState({
    accountNumber: '',
    networkId: '',
    bankName: '',
    accountName: '',
  });

  const { data, isLoading } = useQuery({
    queryKey: ['bank-accounts'],
    queryFn: () => api.bankAccounts.list(),
  });

  // Note: Bank accounts are automatically saved when verified during offramp transactions
  // Direct adding is disabled - use the Ramp page to verify and add accounts
  const addMutation = useMutation({
    mutationFn: (data: typeof formData) => api.bankAccounts.add(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bank-accounts'] });
      setShowAddForm(false);
      setFormData({ accountNumber: '', networkId: '', bankName: '', accountName: '' });
    },
  });

  const removeMutation = useMutation({
    mutationFn: (id: string) => api.bankAccounts.remove(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bank-accounts'] });
    },
  });

  const accounts = data?.data?.accounts || [];

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    addMutation.mutate(formData);
  };

  return (
    <MainLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">Bank Accounts</h1>
            <p className="text-muted-foreground mt-1">
              Your saved bank accounts. Accounts are automatically saved when you verify them during offramp transactions.
            </p>
          </div>
        </div>

        {false && showAddForm && (
          <div className="p-6 bg-card rounded-lg border">
            <h2 className="text-xl font-semibold mb-4">Add Bank Account</h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Account Number</label>
                <input
                  type="text"
                  value={formData.accountNumber}
                  onChange={(e) => setFormData({ ...formData, accountNumber: e.target.value })}
                  className="w-full px-3 py-2 border rounded-md bg-background"
                  placeholder="1234567890"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Bank Code/Network ID</label>
                <input
                  type="text"
                  value={formData.networkId}
                  onChange={(e) => setFormData({ ...formData, networkId: e.target.value })}
                  className="w-full px-3 py-2 border rounded-md bg-background"
                  placeholder="e.g., 044 for Access Bank"
                  required
                />
              </div>
              {addMutation.isError && (
                <div className="p-3 text-sm text-destructive bg-destructive/10 border border-destructive/20 rounded-md">
                  {(addMutation.error as any)?.response?.data?.error || 'Failed to add bank account'}
                </div>
              )}
              <button
                type="submit"
                disabled={addMutation.isPending}
                className="w-full py-2 px-4 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 disabled:opacity-50"
              >
                {addMutation.isPending ? 'Verifying...' : 'Add Account'}
              </button>
            </form>
          </div>
        )}

        <div className="bg-card rounded-lg border">
          {isLoading ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-muted-foreground">Loading...</div>
            </div>
          ) : accounts.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64">
              <div className="text-muted-foreground">No bank accounts added yet</div>
              <button
                onClick={() => setShowAddForm(true)}
                className="mt-4 text-primary hover:underline"
              >
                Add your first account
              </button>
            </div>
          ) : (
            <div className="divide-y">
              {accounts.map((account: any) => (
                <div key={account.id} className="p-4 hover:bg-muted/20">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{account.bankName}</span>
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary/10 text-primary">
                          {account.providerId}
                        </span>
                      </div>
                      <p className="text-sm text-muted-foreground mt-1">
                        {account.accountName}
                      </p>
                      <p className="font-mono text-sm text-muted-foreground mt-1">
                        {account.accountNumber}
                      </p>
                      {account.usageStats && (
                        <p className="text-sm text-muted-foreground mt-2">
                          Used in {account.usageStats.totalTransactions} transaction(s)
                          {account.usageStats.lastUsed && ` • Last used: ${new Date(account.usageStats.lastUsed).toLocaleDateString()}`}
                        </p>
                      )}
                    </div>
                    <button
                      onClick={() => {
                        if (confirm('Are you sure you want to remove this bank account?')) {
                          removeMutation.mutate(account.id);
                        }
                      }}
                      disabled={removeMutation.isPending}
                      className="ml-4 px-3 py-1 text-sm text-destructive hover:bg-destructive/10 rounded-md"
                    >
                      Remove
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </MainLayout>
  );
}
